import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { initials } from "@/lib/formatters";
import { usePermissions } from "@/hooks/usePermissions";
import { listMembers, updateProject } from "@/api/projects";
import { listCampaigns } from "@/api/outreach";
import { useProjectStore } from "@/stores/project.store";
import type { CampaignRead, Project, ProjectMember } from "@/api/types";

const NONE = "__none__";

const schema = z.object({
  name: z.string().min(1, "Name is required").max(255),
  description: z.string(),
  default_prospect_owner_id: z.string(),
  default_client_owner_id: z.string(),
  lemlist_campaign_id: z.string(),
});

type FormValues = z.infer<typeof schema>;

interface ProjectSectionProps {
  project: Project;
}

function readOwnerId(
  config: Record<string, unknown> | undefined,
  key: string,
): string {
  const raw = config?.[key];
  return typeof raw === "string" && raw ? raw : NONE;
}

export function ProjectSection({ project }: ProjectSectionProps) {
  const setCurrentProject = useProjectStore((s) => s.setCurrentProject);
  const setProjects = useProjectStore((s) => s.setProjects);
  const projects = useProjectStore((s) => s.projects);
  const canEditProject = usePermissions().canEditProject;
  const [submitting, setSubmitting] = useState(false);
  const [members, setMembers] = useState<ProjectMember[]>([]);

  const description = (project.config?.description as string | undefined) ?? "";
  const initialProspectOwner = readOwnerId(
    project.config,
    "default_prospect_owner_id",
  );
  const initialClientOwner = readOwnerId(
    project.config,
    "default_client_owner_id",
  );
  const initialCampaignId =
    typeof project.config?.lemlist_campaign_id === "string"
      ? (project.config.lemlist_campaign_id as string)
      : NONE;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: project.name,
      description,
      default_prospect_owner_id: initialProspectOwner,
      default_client_owner_id: initialClientOwner,
      lemlist_campaign_id: initialCampaignId,
    },
  });

  useEffect(() => {
    reset({
      name: project.name,
      description,
      default_prospect_owner_id: initialProspectOwner,
      default_client_owner_id: initialClientOwner,
      lemlist_campaign_id: initialCampaignId,
    });
  }, [
    project.id,
    project.name,
    description,
    initialProspectOwner,
    initialClientOwner,
    initialCampaignId,
    reset,
  ]);

  // Fetch the project members for the owner selects.
  useEffect(() => {
    let cancelled = false;
    listMembers(project.slug)
      .then((m) => {
        if (!cancelled) setMembers(m);
      })
      .catch(() => {
        if (!cancelled) setMembers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [project.slug]);

  // Fetch Lemlist campaigns for the default-campaign select. The fetch is
  // attempted unconditionally so the "Connected" status reflects the live
  // backend state — the API key isn't exposed in `project.config` (it's a
  // secret), so we can't gate on its presence.
  const [campaigns, setCampaigns] = useState<CampaignRead[]>([]);
  const [lemlistStatus, setLemlistStatus] = useState<
    "loading" | "connected" | "disconnected"
  >("loading");
  useEffect(() => {
    let cancelled = false;
    setLemlistStatus("loading");
    listCampaigns(project.slug)
      .then((c) => {
        if (cancelled) return;
        setCampaigns(c);
        setLemlistStatus("connected");
      })
      .catch(() => {
        if (cancelled) return;
        setCampaigns([]);
        setLemlistStatus("disconnected");
      });
    return () => {
      cancelled = true;
    };
  }, [project.slug]);
  const lemlistConnected = lemlistStatus === "connected";

  const prospectOwnerId = watch("default_prospect_owner_id");
  const clientOwnerId = watch("default_client_owner_id");
  const lemlistCampaignId = watch("lemlist_campaign_id");

  async function onSubmit(values: FormValues) {
    setSubmitting(true);
    try {
      const updated = await updateProject(project.slug, {
        name: values.name,
        description: values.description,
        default_prospect_owner_id:
          values.default_prospect_owner_id === NONE
            ? null
            : values.default_prospect_owner_id,
        default_client_owner_id:
          values.default_client_owner_id === NONE
            ? null
            : values.default_client_owner_id,
        lemlist_campaign_id:
          values.lemlist_campaign_id === NONE
            ? null
            : values.lemlist_campaign_id,
      });
      toast.success("Project saved");
      setCurrentProject(updated);
      setProjects(
        projects.map((p) => (p.slug === updated.slug ? updated : p)),
      );
      reset({
        name: updated.name,
        description:
          (updated.config?.description as string | undefined) ?? "",
        default_prospect_owner_id: readOwnerId(
          updated.config,
          "default_prospect_owner_id",
        ),
        default_client_owner_id: readOwnerId(
          updated.config,
          "default_client_owner_id",
        ),
        lemlist_campaign_id:
          typeof updated.config?.lemlist_campaign_id === "string"
            ? (updated.config.lemlist_campaign_id as string)
            : NONE,
      });
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  const memberById = new Map(members.map((m) => [m.id, m]));

  return (
    <section className="space-y-5">
      <header>
        <h2 className="text-lg font-medium tracking-tight text-slate-900">
          Project
        </h2>
        <p className="text-sm text-slate-500">
          Workspace name, description and partner.
        </p>
      </header>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-5 rounded-plane bg-white p-6 shadow-plane"
        noValidate
      >
        <Field label="Name" error={errors.name?.message}>
          <Input
            {...register("name")}
            disabled={!canEditProject}
            className="h-10 rounded-lg border-slate-200"
          />
        </Field>

        <Field label="Description" error={errors.description?.message}>
          <Textarea
            {...register("description")}
            disabled={!canEditProject}
            placeholder="What does this project do?"
            className="min-h-[100px] rounded-lg border-slate-200"
          />
        </Field>

        <div className="grid grid-cols-2 gap-4 border-t border-slate-900/[0.06] pt-5">
          <ReadOnlyField label="Partner" value={project.partner_name} />
          <ReadOnlyField
            label="Project type"
            value={project.project_type.name}
          />
          <ReadOnlyField label="Slug" value={project.slug} mono />
          <ReadOnlyField
            label="Engine"
            value={project.project_type.engine_type}
          />
        </div>

        {/* ── Automatic assignment ────────────────────────────────── */}
        <div className="space-y-4 border-t border-slate-900/[0.06] pt-5">
          <div>
            <h3 className="text-base font-semibold tracking-tight text-slate-900">
              Automatic assignment
            </h3>
            <p className="text-xs text-slate-500">
              Pre-fill the owner on engine-created leads and on deals that
              flip to Won.
            </p>
          </div>

          {canEditProject ? (
            <div className="space-y-4">
              <OwnerField
                label="Default prospect owner"
                helper="Assigned automatically when the engine creates a new company. Manual creations keep their explicit owner."
                value={prospectOwnerId}
                placeholderNone="None (unassigned)"
                members={members}
                onChange={(v) =>
                  setValue("default_prospect_owner_id", v, {
                    shouldDirty: true,
                  })
                }
              />
              <OwnerField
                label="Default client owner"
                helper="Assigned automatically when a prospect transitions to Won. The previous owner is notified of the handoff."
                value={clientOwnerId}
                placeholderNone="None (no change)"
                members={members}
                onChange={(v) =>
                  setValue("default_client_owner_id", v, {
                    shouldDirty: true,
                  })
                }
              />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <ReadOnlyField
                label="Default prospect owner"
                value={
                  prospectOwnerId === NONE
                    ? "None"
                    : memberById.get(prospectOwnerId)?.full_name ?? "—"
                }
              />
              <ReadOnlyField
                label="Default client owner"
                value={
                  clientOwnerId === NONE
                    ? "None"
                    : memberById.get(clientOwnerId)?.full_name ?? "—"
                }
              />
            </div>
          )}
        </div>

        {/* ── Lemlist ─────────────────────────────────────────────── */}
        <div className="space-y-4 border-t border-slate-900/[0.06] pt-5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="text-base font-semibold tracking-tight text-slate-900">
                Lemlist
              </h3>
              <p className="text-xs text-slate-500">
                Default campaign for new email sequences.
              </p>
            </div>
            <LemlistApiBadge status={lemlistStatus} />
          </div>

          {canEditProject ? (
            <div className="space-y-1.5">
              <label className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
                Default campaign
              </label>
              <Select
                value={lemlistCampaignId}
                onValueChange={(v) =>
                  setValue("lemlist_campaign_id", v, { shouldDirty: true })
                }
                disabled={!lemlistConnected}
              >
                <SelectTrigger className="h-12 rounded-lg border-slate-200">
                  {lemlistStatus === "loading" ? (
                    <span className="inline-flex items-center gap-2 text-sm text-slate-500">
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />
                      Loading campaigns…
                    </span>
                  ) : (
                    <SelectValue
                      placeholder={
                        lemlistConnected
                          ? "Select a campaign"
                          : "Configure Lemlist first"
                      }
                    />
                  )}
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>
                    <span className="text-slate-500">None</span>
                  </SelectItem>
                  {campaigns.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      <span className="flex items-center gap-2">
                        <span className="truncate">{c.name}</span>
                        <span className="text-[10px] uppercase tracking-wider text-slate-400">
                          {c.status}
                        </span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                Pre-selected when starting a sequence from a company.
              </p>
            </div>
          ) : (
            <ReadOnlyField
              label="Default campaign"
              value={
                lemlistCampaignId === NONE
                  ? "None"
                  : campaigns.find((c) => c.id === lemlistCampaignId)?.name ??
                    "—"
              }
            />
          )}
        </div>

        {canEditProject && (
          <div className="flex justify-end border-t border-slate-900/[0.06] pt-5">
            <Button
              type="submit"
              variant="primary"
              disabled={!isDirty || submitting}
            >
              {submitting && (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              )}
              Save changes
            </Button>
          </div>
        )}
      </form>
    </section>
  );
}

interface OwnerFieldProps {
  label: string;
  helper: string;
  value: string;
  placeholderNone: string;
  members: ProjectMember[];
  onChange: (v: string) => void;
}

function OwnerField({
  label,
  helper,
  value,
  placeholderNone,
  members,
  onChange,
}: OwnerFieldProps) {
  return (
    <div className="space-y-1.5">
      <label className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
        {label}
      </label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-12 rounded-lg border-slate-200">
          <SelectValue placeholder={placeholderNone} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">
            <span className="text-slate-500">{placeholderNone}</span>
          </SelectItem>
          {members.map((m) => (
            <SelectItem key={m.id} value={m.id}>
              <span className="flex items-center gap-2">
                <Avatar className="h-5 w-5 border border-slate-200">
                  <AvatarFallback className="bg-indigo-100 text-[10px] font-semibold text-indigo-700">
                    {initials(m.full_name)}
                  </AvatarFallback>
                </Avatar>
                <span className="truncate">{m.full_name}</span>
                <span className="text-[10px] uppercase tracking-wider text-slate-400">
                  {m.role.name}
                </span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-xs text-slate-500">{helper}</p>
    </div>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
        {label}
      </label>
      {children}
      {error && <p className="text-xs text-rose-600">{error}</p>}
    </div>
  );
}

function ReadOnlyField({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="space-y-1">
      <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div
        className={
          mono
            ? "font-mono text-sm text-slate-700"
            : "text-sm text-slate-700"
        }
      >
        {value}
      </div>
    </div>
  );
}

function LemlistApiBadge({
  status,
}: {
  status: "loading" | "connected" | "disconnected";
}) {
  if (status === "loading") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-medium text-slate-500">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-slate-400" />
        Checking…
      </span>
    );
  }
  if (status === "connected") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
        Connected ✓
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
      <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
      Not configured
    </span>
  );
}

function extractMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
  }
  return "Couldn't save the project.";
}
